import React, { useEffect, useState, useCallback, useMemo } from "react";
import { FaUserShield, FaSpinner } from "react-icons/fa";
import { Link } from "react-router-dom";

const API_URL = `https://${process.env.REACT_APP_API_KEY_AUTH_BANKER_TRANS}.execute-api.ap-south-1.amazonaws.com/default/bankersTransaction`;
const BANKER_LIST_URL = `https://${process.env.REACT_APP_API_KEY_AUTH_BANKER}.execute-api.ap-south-1.amazonaws.com/default/createBankers`;

export default function BankerLedger() {
  const [transactions, setTransactions] = useState([]);
  const [bankers, setBankers] = useState([]);
  const [selectedBanker, setSelectedBanker] = useState("ALL");
  const [loading, setLoading] = useState(false);

  // Date Range State (Default: Current Month)
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .split("T")[0];
  const lastDay = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDay);

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [txRes, bankerRes] = await Promise.all([
        fetch(API_URL),
        fetch(BANKER_LIST_URL),
      ]);
      const txData = await txRes.json();
      const bankerData = await bankerRes.json();

      setTransactions(txData.transactions || []);
      setBankers(bankerData.branches || []);
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // --- Dynamic Financial Logic ---
  const financials = useMemo(() => {
    let opening = 0;
    let periodIn = 0;
    let periodOut = 0;
    let periodComm = 0;

    // 1. Filter by Selected Banker first
    const bankerTxs = transactions.filter((tx) => {
      if (selectedBanker === "ALL") return true;
      return tx.fromBranch === selectedBanker || tx.toBranch === selectedBanker;
    });

    bankerTxs.forEach((tx) => {
      if (tx.status === "REJECTED" || tx.status === "SPLIT") return;

      const txDate = tx.timestamp.split("T")[0];
      const amt = Number(tx.amount || 0);
      const comm = Number(tx.commission || 0);
      const isMoneyIn = ["IN", "BTIN", "SPLITIN"].includes(tx.paymentType);
      const isMoneyOut = ["OUT", "BTOUT", "SPLITOUT", "EXP"].includes(
        tx.paymentType,
      );

      // A. Calculate Opening Balance (Everything BEFORE Start Date)
      if (txDate < startDate) {
        if (isMoneyIn) opening += amt;
        if (isMoneyOut) opening -= amt;
        opening += comm;
      }
      // B. Calculate Period Activity (Between Start and End Date)
      else if (txDate >= startDate && txDate <= endDate) {
        if (isMoneyIn) periodIn += amt;
        if (isMoneyOut) periodOut += amt;
        periodComm += comm;
      }
    });

    return {
      opening,
      periodIn,
      periodOut,
      periodComm,
      closing: opening + (periodIn - periodOut) + periodComm,
    };
  }, [transactions, selectedBanker, startDate, endDate]);

  // Filtered Table Data
  const tableData = useMemo(() => {
    return transactions
      .filter((tx) => {
        const txDate = tx.timestamp.split("T")[0];
        const dateMatch = txDate >= startDate && txDate <= endDate;
        const bankerMatch =
          selectedBanker === "ALL" ||
          tx.fromBranch === selectedBanker ||
          tx.toBranch === selectedBanker;
        return dateMatch && bankerMatch;
      })
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [transactions, selectedBanker, startDate, endDate]);

  return (
    <div className="admin-container">
      <header className="admin-header">
        <h1>
          <FaUserShield /> Banker Reports
        </h1>
        <div>
          <Link to="/">
            <button className="Branch-nav">Back to Admin</button>
          </Link>
          <button
            onClick={fetchInitialData}
            className="btn-refresh"
            style={{ alignSelf: "flex-end" }}
          >
            <FaSpinner className={loading ? "spin" : ""} /> Update Report
          </button>
        </div>
      </header>

      {/* --- SELECTORS SECTION --- */}
      <section
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "15px",
          padding: "20px",
          background: "#fff",
          borderBottom: "1px solid #eee",
        }}
      >
        <div className="filter-item">
          <label>Select Banker:</label>
          <br />
          <select
            value={selectedBanker}
            onChange={(e) => setSelectedBanker(e.target.value)}
            style={{ padding: "8px", borderRadius: "5px", width: "200px" }}
          >
            <option value="ALL">All Bankers</option>
            {bankers.map((b) => (
              <option key={b.id} value={b.id}>
                {b.id} ({b.name})
              </option>
            ))}
          </select>
        </div>

        <div className="filter-item">
          <label>From Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{ padding: "7px", borderRadius: "5px" }}
          />
        </div>

        <div className="filter-item">
          <label>To Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{ padding: "7px", borderRadius: "5px" }}
          />
        </div>
      </section>

      {/* --- SUMMARY CARDS --- */}
      <section
        className="branch-summary-grid"
        style={{ padding: "20px", gap: "15px" }}
      >
        <div className="branch-summary-card">
          <p>OP Balance (before {startDate})</p>
          <h3>₹{financials.opening.toLocaleString()}</h3>
        </div>
        <div className="branch-summary-card in">
          <p>Period IN</p>
          <h3>₹{financials.periodIn.toLocaleString()}</h3>
        </div>
        <div className="branch-summary-card out">
          <p>Period OUT</p>
          <h3>₹{financials.periodOut.toLocaleString()}</h3>
        </div>
        <div className="branch-summary-card comm">
          <p>Period Comm.</p>
          <h3>₹{financials.periodComm.toLocaleString()}</h3>
        </div>
        <div
          className="branch-summary-card balance"
          style={{ background: "#fffbeb", border: "1px solid #f59e0b" }}
        >
          <p>Closing Balance ({endDate})</p>
          <h3 style={{ color: "#b45309" }}>
            ₹{financials.closing.toLocaleString()}
          </h3>
        </div>
      </section>

      {/* --- LEDGER TABLE --- */}
      <main className="admin-content" style={{ padding: "0 20px" }}>
        <h3>
          {selectedBanker === "ALL"
            ? "Combined Banker Ledger"
            : `${selectedBanker} Ledger View`}
        </h3>
        <div className="table-responsive">
          <table className="branch-table">
            <thead>
              <tr>
                <th>TYPE</th>
                <th>FROM</th>
                <th>TO</th>
                <th>PARTIES</th>
                <th>AMOUNT</th>
                <th>COMM</th>
                <th>STATUS</th>
                <th>DATE</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((tx) => (
                <tr key={tx.paymentId}>
                  <td>
                    <strong className={tx.paymentType}>{tx.paymentType}</strong>
                  </td>
                  <td>{tx.fromBranch}</td>
                  <td>{tx.toBranch}</td>
                  <td>
                    <small>
                      {tx.senderName} ➔ {tx.receiverName}
                    </small>
                  </td>
                  <td>
                    <strong>₹{tx.amount.toLocaleString()}</strong>
                  </td>
                  <td style={{ color: "green" }}>₹{tx.commission}</td>
                  <td>
                    <span className={`status-pill ${tx.status?.toLowerCase()}`}>
                      {tx.status}
                    </span>
                  </td>
                  <td>{tx.timestamp.split("T")[0]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
